import './remirror.css';
import 'remirror/styles/all.css';

import { LinkExtension, MentionExtension, PlaceholderExtension } from 'remirror/extensions';
import { Remirror, useEditorEvent, useRemirror, useRemirrorContext } from '@remirror/react';
import { useField } from 'formik';
import { prosemirrorNodeToHtml } from '@remirror/core';
import React, { forwardRef, memo, useCallback, useImperativeHandle } from 'react';
import { MentionSuggestions } from './MentionSuggestions';

const KeyboardEventBlocker_: React.FC = () => {
  const handleKeyEvent = useCallback((event: KeyboardEvent) => {
    event.stopPropagation();
    return false;
  }, []);
  useEditorEvent('keydown', handleKeyEvent);
  useEditorEvent('keyup', handleKeyEvent);
  return null;
};
const KeyboardEventBlocker = memo(KeyboardEventBlocker_);

const TextEditor_: React.FC = () => {
  const { getRootProps } = useRemirrorContext();
  const rootProps = getRootProps();
  const { key, ...otherProps } = rootProps;

  return <div {...otherProps} />;
};
const TextEditor = memo(TextEditor_);

export type CommentInputHandle = {
  clear: () => void;
};

type CommentInputProps = {
  name: string;
  placeholder: string;
  initialValue?: string;
};

const CommentInput_ = ({ name, placeholder, initialValue }: CommentInputProps, ref: React.Ref<CommentInputHandle>) => {
  const [, , helpers] = useField(name);
  useImperativeHandle(ref, () => ({
    clear: () => {
      setState(manager.createState({ content: '', stringHandler: 'html' }));
    },
  }));

  const linkExtension = new LinkExtension({ autoLink: true });
  linkExtension.addHandler('onClick', (_, data) => {
    window.open(data.href, '_blank');
    return true;
  });

  const mentionExtension = new MentionExtension({
    extraAttributes: { type: 'user' },
    matchers: [{ name: 'at', char: '@', appendText: ' ', matchOffset: 0 }],
  });

  const { manager, state, setState } = useRemirror({
    extensions: () => [linkExtension, new PlaceholderExtension({ placeholder }), mentionExtension],
    content: initialValue || '',
    selection: 'start',
    stringHandler: 'html',
  });

  return (
    <Remirror
      manager={manager}
      initialContent={state}
      state={state}
      onChange={(parameter) => {
        const html = prosemirrorNodeToHtml(parameter.state.doc);
        helpers.setValue(html);
        setState(parameter.state);
      }}
    >
      <KeyboardEventBlocker />
      <TextEditor />
      <MentionSuggestions />
    </Remirror>
  );
};

export const CommentInput = forwardRef(CommentInput_);
